import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'wouter';
import Avatar from '~/components/Avatar';
import Footer from '~/components/Footer';
import Button from '~/components/ui/Button';
import Icon, { IconName } from '~/components/ui/Icon';
import { useBranding } from '~/lib/branding';
import useSheetViewport from '~/lib/useSheetViewport';
import { cn } from '~/lib/utils';
import { signOut } from '~/modules/auth/lib';
import { useAuthorizationStatus } from '~/modules/auth/useAuthorizationStatus';
import useCurrentUser from '~/modules/user/useCurrentUser';

type MenuItemProps = {
  icon: IconName;
  translationKey: string;
  url: string;
};

const menuItemsHero: MenuItemProps[] = [
  {
    icon: 'person',
    translationKey: 'profile',
    url: '/profile',
  },
  // {
  //   translationKey: 'interface',
  //   url: '/interface',
  // },
  {
    icon: 'paid',
    translationKey: 'earning',
    url: '/earning',
  },
  // {
  //   translationKey: 'subscription',
  //   url: '/subscription',
  // },
  {
    icon: 'support',
    translationKey: 'support',
    url: '/support',
  },
  {
    icon: 'database',
    translationKey: 'dataManagement',
    url: '/data-management',
  },
];

const menuItemsChatoshi: MenuItemProps[] = [
  {
    icon: 'person',
    translationKey: 'profile',
    url: '/profile',
  },
  {
    icon: 'support',
    translationKey: 'support',
    url: '/support',
  },
  {
    icon: 'database',
    translationKey: 'dataManagement',
    url: '/data-management',
  },
];

function MenuItem({ item, onClick }: { item: MenuItemProps; onClick?: () => void }) {
  const { t } = useTranslation('', { keyPrefix: 'layout.menu' });
  const [location] = useLocation();
  const isActive = location.startsWith(item.url);

  return (
    <Link
      to={item.url}
      className={cn(
        'flex min-h-12 items-center gap-3 rounded-xl border border-transparent px-4 py-3 text-[13px] phone:text-[14px] tablet:text-[15px] text-gray-3 transition-colors hover:bg-row-hover hover:text-body',
        {
          'border-nav-active-border bg-linear-to-r from-nav-active-from to-nav-active-to text-nav-active-text':
            isActive,
        },
      )}
      onClick={onClick}
    >
      <Icon name={item.icon} className="text-[21px]" />
      <span>{t(item.translationKey)}</span>
      {isActive && <Icon name="chevron_right" className="ml-auto text-[19px]" />}
    </Link>
  );
}

function SidebarIdentity() {
  const user = useCurrentUser();
  const displayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || '—';
  const handle = user?.username ? `@${user.username.replace(/^@/, '')}` : '—';

  return (
    <div className="mx-3 mb-12 flex min-w-0 items-center gap-4">
      <Avatar url={user?.avatar} size={42} className="shrink-0" />
      <div className="min-w-0 ">
        <strong className="block truncate text-[15px] font-semibold text-foreground">
          {displayName}
        </strong>
        <div className="truncate text-[13px] text-gray-3">{handle}</div>
      </div>
    </div>
  );
}

export function SideMenu({ className, onClick }: { className?: string; onClick?: () => void }) {
  const branding = useBranding();
  const menuItems = branding === 'hero' ? menuItemsHero : menuItemsChatoshi;
  return (
    <div className={cn('flex flex-col', className)}>
      <SidebarIdentity />
      <p className="mb-3.5 px-4 text-[12px] tracking-[1.8px] text-nav-label">ACCOUNT</p>
      <div className="flex flex-col gap-2">
        {menuItems.map((item) => (
          <Fragment key={item.translationKey}>
            <MenuItem item={item} onClick={onClick} />
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function getDashboardHref() {
  if (window.__PXBROWSER__) {
    return window.__CONFIG.VITE_DASHBOARD_URL;
  }
  return '/';
}

export function HeaderLogo() {
  const branding = useBranding();
  const href = getDashboardHref();

  const logo = (
    <Icon
      name={branding === 'chatoshi' ? 'chatoshiLogo' : 'heroLogo'}
      className="h-12 w-29 compact:h-auto compact:w-20 max-xs:w-17"
      aria-label={branding === 'chatoshi' ? 'Chatoshi' : 'Hero'}
      role="img"
    />
  );

  return href === '/' ? <Link to="/">{logo}</Link> : <a href={href}>{logo}</a>;
}

function BaseLayout({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('', { keyPrefix: 'layout.header' });
  const { isAuthorized } = useAuthorizationStatus();
  const [location] = useLocation();
  const isAccount = location === '/account' || location === '/';
  useSheetViewport();

  return (
    <div className="flex min-h-dvh max-w-300 flex-col items-center gap-17 mx-auto relative px-4 sm:px-6 xl:px-0 compact:max-w-180 compact:px-4 compact:gap-3 max-xs:px-3">
      <header className="page-header-glow relative flex justify-between items-center w-full pt-6 sm:pt-10 compact:min-h-17 compact:gap-3 compact:pt-[env(safe-area-inset-top)]">
        <div
          className={cn(
            'flex items-center gap-4 compact:gap-1.5',
            isAuthorized && !isAccount && 'compact:hidden',
          )}
        >
          <HeaderLogo />
          {isAuthorized && (
            <span className="hidden compact:inline text-[15px] text-body">
              {t('account.title', { keyPrefix: '' })}
            </span>
          )}
        </div>
        {isAuthorized && !isAccount && (
          <Link
            to="/account"
            className="hidden compact:inline-flex min-h-11 items-center gap-2 text-sm text-body"
          >
            <Icon name="arrowBack" className="text-[18px]" />
            {t('account.title', { keyPrefix: '' })}
          </Link>
        )}

        {isAuthorized && (
          <Button
            size="sm"
            variant="outlined"
            className="gap-2 compact:size-10 compact:p-0"
            aria-label={t('signOut')}
            onPress={signOut}
          >
            <Icon name="logout" className="text-[18px]" />
            <span className="compact:hidden">{t('signOut')}</span>
          </Button>
        )}
      </header>

      <main className="flex-1 w-full flex flex-col">
        <div className="h-full flex-1 flex flex-col">
          <div
            className={cn('flex-1 w-full', {
              'desktop:grid desktop:grid-cols-[260px_minmax(0,1fr)] gap-16': isAuthorized,
            })}
          >
            {isAuthorized && <SideMenu className="hidden desktop:block" />}
            <div className="flex min-w-0 flex-col compact:pb-7">{children}</div>
          </div>
        </div>
      </main>

      <Footer className="flex-0" />
    </div>
  );
}

export default BaseLayout;
